/**
 * One workflow: build it as a list read top to bottom (when, only if, then
 * each step in order, then its goals), test it on a record before saving,
 * enrol people from a segment, and see its runs and how it is doing.
 *
 * Two views of the same draft, switched with Designer | List (remembered
 * on this device). The designer (`designer/`) draws it top to bottom as
 * boxes, to drag steps onto and move about; it is the default on a wide
 * screen and loads only when shown. The list says it all, works from the
 * keyboard and with a screen reader, and reads the same on a phone, where
 * it is the only view (below 768px wide): an "If / else" step indents its
 * Yes and No steps under it. Both edit the same draft, and Undo and Redo
 * work in either. Where boxes sit is worked out, never stored.
 * `/automations/new` is a blank one, made on first save; every workflow
 * starts off and is switched on by hand.
 *
 * Saving sends the trigger, conditions and steps whole, with the daily
 * limit on emails (the Sending card, shown when it sends email). Any change
 * to the definition makes a new version on the server (the limit does
 * not); runs already going finish on the version they started on. Before a save the server is asked whether a
 * switched-on workflow already does this (same trigger, conditions and
 * first action): if so, "This looks like X. Edit that one instead?". An
 * amber note says when this one can email someone on the same day as
 * another. A new workflow's name is suggested from its trigger and first
 * step until someone types one.
 */
import { lazy, Suspense, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Box, Button, Card, Grid, Group, Loader, Menu, Modal, SegmentedControl, Stack,
  Switch, Tabs,
  Text, TextInput, Textarea, Tooltip, UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArchive, IconArrowBackUp, IconArrowForwardUp, IconArrowLeft, IconCopy, IconDots, IconFolder, IconUsersPlus,
} from '@tabler/icons-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePipelines } from '../../api/crm';
import { useCampaigns, useEmailTemplates } from '../../api/queries';
import {
  useCheckWorkflow, useEnrolments, useWorkflow, useWorkflowEdits, useWorkflows, type StepKind, type Workflow, type WorkflowGoal,
  type WorkflowMatch, type WorkflowOverlap, type WorkflowStep, type WorkflowTrigger,
} from '../../api/workflows';
import { TOKENS } from '../../theme';
import { useVocab } from '../../vocab';
import { asFilters, countIncomplete, toStored, type SmartFilter } from '../SmartFilterEditor';
import { useOwnerOptions } from '../crm/crmShared';
import { AnalyticsPanel } from './AnalyticsPanel';
import { canRedo, canUndo, record, redo, reset, startHistory, undo, type History } from './history';
import { ConditionsCard } from './ConditionsCard';
import { EnrolDialog } from './EnrolDialog';
import { GoalsCard } from './GoalsCard';
import { RunHistory } from './RunHistory';
import { Connector, StepList, type TreeContext } from './StepCard';
import { suggestName } from './listModel';
import { parseRamp, showRamp } from '../emails/warmup';
import { MAX_DAILY_LIMIT, SendingCard } from './SendingCard';
import { TestPanel } from './TestPanel';
import { SendToSegment } from './SendToSegment';
import { TriggerCard } from './TriggerCard';
import { triggerSentence } from './designer/toDesigner';
import {
  addStepAt, allSteps, blankStep, blankTrigger, moveStep, removeStep, sendsEmail, stepProblems,
  triggerLive, triggerRecordType, updateStep, type ListRef,
} from './workflowModel';

type Draft = {
  name: string;
  description: string;
  trigger: WorkflowTrigger;
  andFilters: SmartFilter[];
  orFilters: SmartFilter[];
  steps: WorkflowStep[];
  goals: WorkflowGoal[];
  /** Its own daily limit on emails; 0 for none. */
  dailyLimit: number;
  /** Its warm-up ramp as typed: "200, 500, 1000". */
  warmup: string;
};

/** The highest daily limit the server takes (workflows/mod.rs MAX_DAILY_SEND_LIMIT), and the most for one warm-up day. */

// The designer and its library load only when someone opens it.
const WorkflowDesigner = lazy(() => import('./designer/WorkflowDesigner'));

/** Where the Designer | List choice is kept, per device. */
export const VIEW_KEY = 'hawfinch.workflows.view';
type View = 'designer' | 'list';

/** The view chosen on this device: the designer unless the list was. */
function storedView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'designer';
  } catch {
    return 'designer';
  }
}

function storeView(view: View) {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* a private window: it just is not remembered */
  }
}

function draftOf(w: Workflow | null): Draft {
  return {
    name: w?.name ?? '',
    description: w?.description ?? '',
    trigger: w?.trigger ?? blankTrigger('submission_arrived'),
    andFilters: asFilters(w?.conditions, 'and'),
    orFilters: asFilters(w?.conditions, 'or'),
    steps: w?.steps ?? [],
    goals: w?.goals ?? [],
    dailyLimit: w?.send_limit?.daily ?? 0,
    warmup: showRamp(w?.send_limit?.warmup),
  };
}

/** The draft as the server stores it, for comparing and saving. */
function definitionOf(d: Draft) {
  return {
    name: d.name.trim(),
    description: d.description.trim(),
    trigger: d.trigger,
    conditions: toStored(d.andFilters, d.orFilters),
    steps: d.steps,
    goals: d.goals,
    // Changing it makes no new version on the server: it is not behaviour.
    send_limit: d.dailyLimit > 0 || parseRamp(d.warmup).length > 0
      ? { daily: d.dailyLimit, warmup: parseRamp(d.warmup) }
      : null,
  };
}

export function WorkflowBuilderPage() {
  const { id: rawId } = useParams();
  const isNew = rawId === 'new';
  const id = isNew ? null : (rawId ?? null);
  const list = useWorkflows();
  const workflow = useWorkflow(id);

  if (!isNew && workflow.error) {
    return (
      <Stack gap="md">
        <BackLink />
        <Alert color="red" variant="light">{workflow.error.message}</Alert>
      </Stack>
    );
  }
  if ((!isNew && workflow.isPending) || list.isPending) {
    return <Group justify="center" p="xl"><Loader /></Group>;
  }
  return <Builder key={workflow.data?.id ?? 'new'} saved={isNew ? null : (workflow.data ?? null)} />;
}

function BackLink() {
  const v = useVocab();
  return (
    <Anchor component={Link} to="/automations" size="sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: TOKENS.textSecondary }}>
      <IconArrowLeft size={14} />{v.Many('workflow')}
    </Anchor>
  );
}

function Builder({ saved }: { saved: Workflow | null }) {
  const v = useVocab();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const list = useWorkflows();
  const edits = useWorkflowEdits();
  const pipelines = usePipelines().data?.pipelines;
  const narrow = useMediaQuery('(max-width: 75em)') ?? false;
  const canManage = list.data?.can_manage ?? false;
  const smsAvailable = list.data?.sms_available ?? false;

  // The draft, with Undo and Redo (history.ts).
  const [hist, setHist] = useState<History<Draft>>(() => startHistory(draftOf(saved)));
  const draft = hist.present;
  const setDraft = useCallback((u: Draft | ((d: Draft) => Draft)) => {
    setHist((h) => record(h, typeof u === 'function' ? u(h.present) : u, Date.now()));
  }, []);
  const [view, setView] = useState<View>(storedView);
  const phone = useMediaQuery('(max-width: 767px)') ?? false;
  const shown: View = phone ? 'list' : view;
  const [baseline, setBaseline] = useState(() => JSON.stringify(definitionOf(draftOf(saved))));
  const lastSaved = useRef(baseline);
  const [error, setError] = useState('');
  useEffect(() => {
    // A save hands back the stored workflow (step ids filled in, trigger
    // trimmed): that is the new starting point. Switching it on or off
    // changes no definition, so edits in hand are kept.
    if (!saved) return;
    const d = draftOf(saved);
    const next = JSON.stringify(definitionOf(d));
    if (next === lastSaved.current) return;
    lastSaved.current = next;
    setHist(reset(d));
    setBaseline(next);
  }, [saved]);

  const def = definitionOf(draft);
  const dirty = JSON.stringify(def) !== baseline;
  const rt = triggerRecordType(draft.trigger);
  const wanted = params.get('tab');
  const tab = saved && (wanted === 'runs' || wanted === 'analytics') ? wanted : 'build';
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const [enrolOpen, setEnrolOpen] = useState(false);
  const check = useCheckWorkflow();
  const [lookalike, setLookalike] = useState<{ duplicates: WorkflowMatch[]; overlaps: WorkflowOverlap[] } | null>(null);
  const [nameTouched, setNameTouched] = useState(!!saved);
  const summary = saved ? list.data?.workflows.find((w) => w.id === saved.id) : undefined;
  const overlaps = summary?.overlaps ?? [];
  // The notes over the chart, and which of them have been put away on this visit.
  type NoteKind = 'readonly' | 'archived' | 'overlap';
  const notes: NoteKind[] = [
    ...(!canManage ? ['readonly' as const] : []),
    ...(saved?.archived_at ? ['archived' as const] : []),
    ...(overlaps.length > 0 ? ['overlap' as const] : []),
  ];
  const [dismissed, setDismissed] = useState<NoteKind[]>([]);
  const dismiss = (n: NoteKind) => setDismissed((d) => [...d, n]);

  const stageKind = useMemo(() => {
    const kinds = new Map<string, string>();
    for (const p of pipelines ?? []) for (const s of p.stages) kinds.set(s.id, s.kind);
    return (sid: string) => kinds.get(sid) ?? null;
  }, [pipelines]);

  // The steps are a tree; every change is by step id.
  const editSteps = (f: (steps: WorkflowStep[]) => WorkflowStep[]) => setDraft((d) => ({ ...d, steps: f(d.steps) }));
  const all = useMemo(() => allSteps(draft.steps), [draft.steps]);
  const numbers = useMemo(() => new Map(all.map((s, i) => [s.id, i + 1])), [all]);
  const problems = useMemo(() => stepProblems(v, draft.steps, rt, stageKind), [v, draft.steps, rt, stageKind]);

  // Ctrl or Cmd + Z, and Shift + Z or Y to redo; not while typing, where
  // the field's own undo applies.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('input, textarea, select, [contenteditable="true"]'))) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        setHist(undo);
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        setHist(redo);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const ctx: TreeContext = {
    rt,
    canManage,
    smsAvailable,
    webhookSecret: saved?.webhook_secret,
    list: list.data,
    numbers,
    all,
    ops: {
      change: (id, next) => editSteps((steps) => updateStep(steps, id, () => next)),
      move: (id, by) => editSteps((steps) => moveStep(steps, id, by)),
      remove: (id) => editSteps((steps) => removeStep(steps, id)),
      add: (at: ListRef, kind: StepKind) => editSteps((steps) => addStepAt(steps, at, blankStep(kind, steps, rt))),
    },
  };

  async function save() {
    setError('');
    if (!def.name) return setError(`Give the ${v.one('workflow')} a name.`);
    if (!triggerLive(def.trigger.kind)) return setError('Forms are not here yet. Choose another trigger.');
    if (def.steps.length === 0) return setError('Add at least one step.');
    if (countIncomplete([...draft.andFilters, ...draft.orFilters]) > 0) return setError('Every field condition needs a field.');
    if (problems.length > 0) return setError(problems.join(' '));
    if (parseRamp(draft.warmup).some((n) => n > MAX_DAILY_LIMIT)) {
      return setError(`Each warm-up day can be at most ${MAX_DAILY_LIMIT.toLocaleString('en-GB')} emails.`);
    }
    // Does a switched-on workflow already do this? Asked of the server, by
    // the rule in workflows/organise.rs. A check that fails never stops a save.
    try {
      const found = await check.mutateAsync({
        id: saved?.id ?? null, trigger: def.trigger, conditions: def.conditions, steps: def.steps,
      });
      if (found.duplicates.length > 0) {
        setLookalike(found);
        return;
      }
    } catch {
      /* save anyway */
    }
    await commit();
  }

  async function commit() {
    setLookalike(null);
    try {
      if (saved) {
        // No limit is sent as 0 and no ramp: an absent one leaves the stored limit as it is.
        const w = await edits.update.mutateAsync({ id: saved.id, body: { ...def, send_limit: def.send_limit ?? { daily: 0, warmup: [] } } });
        notifications.show({ color: 'teal', title: 'Saved', message: `${w.name}, version ${w.version}` });
      } else {
        const w = await edits.create.mutateAsync({ ...def, enabled: false });
        notifications.show({ color: 'teal', title: `${v.One('workflow')} made`, message: `${w.name}. It is off until you switch it on.` });
        navigate(`/automations/${encodeURIComponent(w.id)}`, { replace: true });
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  /** A switched-off copy with no history: how a send goes again to people this one already emailed. */
  async function duplicate() {
    if (!saved) return;
    try {
      const copy = await edits.duplicate.mutateAsync(saved.id);
      notifications.show({ color: 'teal', title: 'Copied', message: `${copy.name}. The copy is off and has sent nothing yet.` });
      navigate(`/automations/${encodeURIComponent(copy.id)}`);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not copied', message: (e as Error).message });
    }
  }

  async function toggle(enabled: boolean) {
    if (!saved) return;
    try {
      await edits.setEnabled.mutateAsync({ id: saved.id, enabled });
      notifications.show({ color: 'teal', title: enabled ? 'Switched on' : 'Switched off', message: saved.name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
    }
  }

  const savedRt = saved ? triggerRecordType(saved.trigger) : rt;
  const triggerCard = (
    <TriggerCard
      trigger={draft.trigger}
      onChange={(trigger) => set({ trigger })}
      extra={draft.trigger.kind === 'enrolment' && (!saved || saved.trigger.kind === 'enrolment') ? (
        <SendToSegment
          workflowId={saved?.id ?? null}
          enabled={!!saved?.enabled}
          sendsEmail={sendsEmail(saved?.steps ?? [])}
          recordType={savedRt}
          dirty={dirty}
          canManage={canManage}
        />
      ) : undefined}
    />
  );
  const conditionsCard = (
    <ConditionsCard
      rt={rt}
      andFilters={draft.andFilters}
      orFilters={draft.orFilters}
      setAndFilters={(andFilters) => set({ andFilters })}
      setOrFilters={(orFilters) => set({ orFilters })}
      list={list.data}
    />
  );

  const statusSentence = saved
    ? (saved.enabled
      ? `On. ${dirty ? 'Runs use the saved version until you save.' : 'Runs whenever its trigger happens.'}`
      : 'Off. Nothing runs until it is switched on.')
    : 'Not made yet. It starts off once you make it.';
  const details = (
    <DetailsFields
      description={draft.description}
      onDescription={(description) => set({ description })}
      canManage={canManage}
      folder={summary?.folder ?? ''}
      owner={summary?.owner_name ?? ''}
      status={statusSentence}
    />
  );
  const goalsCard = (
    <GoalsCard
      goals={draft.goals}
      onChange={(goals) => set({ goals })}
      rt={rt}
      list={list.data}
      sendsEmail={sendsEmail(draft.steps)}
      canManage={canManage}
    />
  );
  const sendingCard = sendsEmail(draft.steps) ? (
    <SendingCard
      dailyLimit={draft.dailyLimit}
      onChange={(dailyLimit) => set({ dailyLimit })}
      warmup={draft.warmup}
      onWarmup={(warmup) => set({ warmup })}
    />
  ) : null;
  const testPanel = (
    <TestPanel
      workflowId={saved?.id ?? null}
      rt={rt}
      trigger={def.trigger}
      conditions={def.conditions}
      steps={def.steps}
      goals={def.goals}
      canManage={canManage}
    />
  );
  const fieldset = (children: ReactNode) => (
    <fieldset disabled={!canManage} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>{children}</fieldset>
  );

  // The designer's Settings panel: everything about the automation that is not a step or its trigger.
  const settingsPanel = (
    <Stack gap="lg" data-testid="settings-panel">
      <SettingsSection title="Details" id="details">{fieldset(details)}</SettingsSection>
      {sendingCard && <SettingsSection title="Sending" id="sending">{fieldset(sendingCard)}</SettingsSection>}
      <SettingsSection title="Stop when" id="goals">{fieldset(goalsCard)}</SettingsSection>
      <SettingsSection title="Test" id="test">{testPanel}</SettingsSection>
    </Stack>
  );

  // The trigger in words for the designer's first box, with its next send when sent from a segment.
  const names = useNames();
  const enrolling = draft.trigger.kind === 'enrolment' && saved?.trigger.kind === 'enrolment';
  const schedules = useEnrolments(shown === 'designer' && enrolling ? saved.id : null).data?.schedules;
  const nextSend = useMemo(() => (schedules ?? [])
    .filter((s) => (s.status ?? 'active') === 'active' && s.next_run_at)
    .sort((a, b) => String(a.next_run_at).localeCompare(String(b.next_run_at)))[0] ?? null, [schedules]);
  const triggerWords = triggerSentence(v, draft.trigger, names, draft.andFilters, draft.orFilters, nextSend);

  const build = shown === 'designer' ? (
    // Outside any fieldset: a viewer still zooms and pans, and the panel disables its own settings.
    <Suspense fallback={<Group justify="center" p="xl"><Loader size="sm" /></Group>}>
      <WorkflowDesigner
        steps={draft.steps}
        onSteps={(steps) => set({ steps })}
        ctx={ctx}
        triggerTitle={triggerWords}
        triggerPanel={<Stack gap={0}>{triggerCard}<Connector />{conditionsCard}</Stack>}
        settingsPanel={settingsPanel}
        workflowId={saved?.id ?? null}
        problems={problems}
      />
    </Suspense>
  ) : (
    <Grid gutter="lg">
      <Grid.Col span={narrow ? 12 : 7.5}>
        {fieldset(
          <Stack gap={0}>
            <Card padding="sm" withBorder mb="md" data-testid="details-card">{details}</Card>
            {triggerCard}
            <Connector />
            {conditionsCard}
            <StepList steps={draft.steps} at={{ parentId: null }} depth={0} ctx={ctx} firstLabel="Then" />
            {draft.steps.length === 0 && (
              <Text size="sm" c="dimmed" mt="xs">No steps yet. It needs at least one.</Text>
            )}
            <Connector label="Until" />
            {goalsCard}
            {sendingCard && <Box mt="md">{sendingCard}</Box>}
          </Stack>,
        )}
      </Grid.Col>
      <Grid.Col span={narrow ? 12 : 4.5}>
        <Box style={narrow ? undefined : { position: 'sticky', top: 16 }}>{testPanel}</Box>
      </Grid.Col>
    </Grid>
  );

  const canEnrol = !!saved && canManage && (rt === 'person' || rt === 'submission');
  const busy = check.isPending || edits.update.isPending || edits.create.isPending;
  const visibleNotes = notes.filter((n) => !dismissed.includes(n));

  return (
    <Tabs
      value={tab}
      onChange={(t) => {
        const next = new URLSearchParams(params);
        if (t === 'runs' || t === 'analytics') next.set('tab', t);
        else next.delete('tab');
        setParams(next, { replace: true });
      }}
      keepMounted={false}
      variant="pills"
      vars={() => ({ root: { '--tabs-color': TOKENS.text, '--tabs-text-color': TOKENS.bg } })}
      radius="sm"
      styles={{ tab: { padding: '4px 10px', fontSize: 13, height: 30 } }}
    >
      <Stack gap="xs">
        {/* One row: where it is, what it is called, and everything done to it. Wraps on a narrow screen. */}
        <Group justify="space-between" align="center" wrap="wrap" gap="xs" data-testid="builder-header">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0, flex: '1 1 260px' }}>
            <Tooltip label={`All ${v.many('workflow')}`} openDelay={400}>
              <ActionIcon component={Link} to="/automations" variant="subtle" color="gray" aria-label={v.Many('workflow')} data-testid="back-link">
                <IconArrowLeft size={18} />
              </ActionIcon>
            </Tooltip>
            <InlineName
              value={draft.name}
              placeholder={`New ${v.one('workflow')}`}
              canEdit={canManage}
              suggested={!saved && !nameTouched}
              onChange={(name) => { setNameTouched(true); set({ name }); }}
            />
            {saved && <Badge variant="light" color="gray" radius="sm" tt="none" style={{ flexShrink: 0 }} data-testid="workflow-version">{`Version ${saved.version}`}</Badge>}
            {dirty && <Badge variant="light" color="chartAmber" radius="sm" tt="none" style={{ flexShrink: 0 }}>Unsaved changes</Badge>}
            {(summary?.folder || summary?.owner_name) && (
              <Tooltip
                label={[summary.folder && `Folder: ${summary.folder}`, summary.owner_name && `${v.One('owner')}: ${summary.owner_name}`].filter(Boolean).join(' · ')}
                withArrow
              >
                <Box component="span" style={{ display: 'inline-flex', color: TOKENS.textSecondary, flexShrink: 0 }} data-testid="workflow-filed">
                  <IconFolder size={16} />
                </Box>
              </Tooltip>
            )}
          </Group>
          <Group gap={6} wrap="wrap" justify="flex-end">
            {saved && (
              <Tabs.List style={{ flexWrap: 'nowrap' }}>
                <Tabs.Tab value="build">Build</Tabs.Tab>
                <Tabs.Tab value="runs">{v.Many('run')}</Tabs.Tab>
                <Tabs.Tab value="analytics" data-testid="analytics-tab">Analytics</Tabs.Tab>
              </Tabs.List>
            )}
            {tab === 'build' && (
              <SegmentedControl
                size="xs"
                value={shown}
                onChange={(x) => {
                  const next = x === 'designer' ? 'designer' : 'list';
                  setView(next);
                  storeView(next);
                }}
                data={[
                  { value: 'designer', label: phone ? 'Designer (wider screens)' : 'Designer', disabled: phone },
                  { value: 'list', label: 'List' },
                ]}
                aria-label="Show the steps as"
                data-testid="builder-view"
              />
            )}
            {tab === 'build' && canManage && (
              <Group gap={2} wrap="nowrap">
                <Tooltip label="Undo" openDelay={400}>
                  <ActionIcon variant="subtle" color="gray" aria-label="Undo" disabled={!canUndo(hist)} onClick={() => setHist(undo)} data-testid="undo">
                    <IconArrowBackUp size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Redo" openDelay={400}>
                  <ActionIcon variant="subtle" color="gray" aria-label="Redo" disabled={!canRedo(hist)} onClick={() => setHist(redo)} data-testid="redo">
                    <IconArrowForwardUp size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
            {saved && canManage && (
              <Tooltip label="Duplicate: a switched-off copy with no history" openDelay={400}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="Duplicate"
                  onClick={duplicate}
                  loading={edits.duplicate.isPending}
                  data-testid="duplicate-workflow"
                >
                  <IconCopy size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {canEnrol && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" aria-label="More" data-testid="builder-more">
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconUsersPlus size={14} />}
                    onClick={() => setEnrolOpen(true)}
                    disabled={!saved?.enabled}
                    title={saved?.enabled ? undefined : 'Switch it on first'}
                    data-testid="enrol-open"
                  >
                    {`Enrol ${rt === 'person' ? v.many('person') : v.many('lead')}${saved?.enabled ? '' : ' (switch it on first)'}`}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
            {saved && (
              <Tooltip label={statusSentence} multiline w={240} withArrow openDelay={300}>
                <div>
                  <Switch
                    label="On"
                    checked={saved.enabled}
                    disabled={!canManage || edits.setEnabled.isPending || !!saved.archived_at}
                    onChange={(e) => toggle(e.currentTarget.checked)}
                    data-testid="workflow-enabled"
                  />
                </div>
              </Tooltip>
            )}
            {canManage && (
              <Button size="xs" onClick={save} loading={busy} disabled={!!saved && !dirty} data-testid="save-workflow">
                {saved ? 'Save' : `Make the ${v.one('workflow')}`}
              </Button>
            )}
          </Group>
        </Group>
        {!saved && !nameTouched && (
          <NameSuggester
            trigger={draft.trigger}
            steps={draft.steps}
            name={draft.name}
            onName={(name) => setDraft((d) => ({ ...d, name }))}
          />
        )}

        {/* Only what is there, one line each. Notes that change nothing can be put away. */}
        {visibleNotes.includes('readonly') && (
          <Note color="gray" onClose={() => dismiss('readonly')} testid="readonly-note">
            {`You can look but not change: managing ${v.many('workflow')} needs permission.`}
          </Note>
        )}
        {visibleNotes.includes('archived') && (
          <Note color="gray" icon={<IconArchive size={14} />} onClose={() => dismiss('archived')} testid="archived-note">
            {`Archived. It is off and hidden from the list; restore it from the ${v.Many('workflow')} page to switch it on.`}
          </Note>
        )}
        {visibleNotes.includes('overlap') && (
          <Note color="chartAmber" icon={<IconAlertTriangle size={14} />} onClose={() => dismiss('overlap')} testid="builder-overlap-note">
            {'Can email the same person on the same day as '}
            {overlaps.map((o, i) => (
              <span key={o.id}>
                {i > 0 && (i === overlaps.length - 1 ? ' and ' : ', ')}
                <Anchor component={Link} to={`/automations/${encodeURIComponent(o.id)}`} size="sm" c="inherit" fw={600} underline="always">
                  {`'${o.name}'`}
                </Anchor>
              </span>
            ))}
            {'. Add a wait, or narrow the trigger, if that is not meant.'}
          </Note>
        )}
        {error && <Note color="red" onClose={() => setError('')} testid="save-error">{error}</Note>}

        <Tabs.Panel value="build">{build}</Tabs.Panel>
        {saved && (
          <Tabs.Panel value="runs" pt="xs">
            <RunHistory workflowId={saved.id} />
          </Tabs.Panel>
        )}
        {saved && (
          <Tabs.Panel value="analytics" pt="xs">
            <AnalyticsPanel workflowId={saved.id} steps={saved.steps} canManage={canManage} />
          </Tabs.Panel>
        )}
      </Stack>
      {lookalike && (
        <Modal
          opened
          onClose={() => setLookalike(null)}
          title={lookalike.duplicates[0] ? `This looks like '${lookalike.duplicates[0].name}'` : ''}
          size="md"
        >
          <Stack gap="sm" data-testid="duplicate-warning">
            <Text size="sm">
              {`${lookalike.duplicates.length > 1 ? `${lookalike.duplicates.length} switched-on ${v.many('workflow')} have` : `A switched-on ${v.one('workflow')} has`} the same trigger, conditions and first step. Edit that one instead?`}
            </Text>
            <Stack gap={4}>
              {lookalike.duplicates.map((d) => (
                <Text key={d.id} size="sm" fw={600}>{d.folder ? `${d.name} (${d.folder})` : d.name}</Text>
              ))}
            </Stack>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={commit} data-testid="save-anyway">Save anyway</Button>
              <Button
                onClick={() => {
                  const first = lookalike.duplicates[0]!;
                  setLookalike(null);
                  navigate(`/automations/${encodeURIComponent(first.id)}`);
                }}
                data-testid="open-lookalike"
              >
                {`Open '${lookalike.duplicates[0]!.name}'`}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
      {saved && (
        <EnrolDialog
          opened={enrolOpen}
          onClose={() => setEnrolOpen(false)}
          workflowId={saved.id}
          recordType={rt}
        />
      )}
    </Tabs>
  );
}

/** A note between the header and the chart: one line, closable when putting it away changes nothing. */
function Note({
  color, icon, onClose, testid, children,
}: { color: string; icon?: ReactNode; onClose?: () => void; testid: string; children: ReactNode }) {
  return (
    <Alert
      color={color}
      variant="light"
      py={4}
      px="xs"
      icon={icon}
      withCloseButton={!!onClose}
      onClose={onClose}
      closeButtonLabel="Put this away"
      data-testid={testid}
      styles={{ icon: { marginRight: 6 }, closeButton: { width: 20, height: 20, minWidth: 20, minHeight: 20 } }}
    >
      <Text size="sm">{children}</Text>
    </Alert>
  );
}

/** One heading in the Settings panel. */
function SettingsSection({ title, id, children }: { title: string; id: string; children: ReactNode }) {
  return (
    <Stack gap={6} data-testid={`settings-${id}`}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>{title}</Text>
      {children}
    </Stack>
  );
}

/** The description, and where it is filed and whether it runs, which are changed elsewhere. */
function DetailsFields({
  description, onDescription, canManage, folder, owner, status,
}: {
  description: string; onDescription: (s: string) => void; canManage: boolean; folder: string; owner: string; status: string;
}) {
  const v = useVocab();
  return (
    <Stack gap={6}>
      <Textarea
        size="xs"
        label="Description"
        placeholder="What it is for, for whoever reads it next."
        autosize
        minRows={1}
        value={description}
        onChange={(e) => onDescription(e.currentTarget.value)}
        readOnly={!canManage}
        data-testid="workflow-description"
      />
      <Group gap={12} wrap="wrap" data-testid="workflow-details">
        <Text size="xs" c="dimmed" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <IconFolder size={12} />{folder || 'No folder'}
        </Text>
        {owner && <Text size="xs" c="dimmed">{`${v.One('owner')}: ${owner}`}</Text>}
        <Text size="xs" c="dimmed">{status}</Text>
      </Group>
    </Stack>
  );
}

/**
 * The name as the page's heading: click it (or tab to it and press Enter) to
 * change it; Enter or leaving keeps the change, Escape puts it back. A new
 * automation's suggested name says so.
 */
function InlineName({
  value, placeholder, canEdit, suggested, onChange,
}: { value: string; placeholder: string; canEdit: boolean; suggested: boolean; onChange: (s: string) => void }) {
  const [editing, setEditing] = useState(false);
  const before = useRef(value);
  const heading = {
    fontSize: 20, fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.02em', color: TOKENS.text,
  } as const;
  if (editing) {
    return (
      <TextInput
        autoFocus
        variant="unstyled"
        aria-label="Name"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setEditing(false);
          if (e.key === 'Escape') {
            if (value !== before.current) onChange(before.current);
            setEditing(false);
          }
        }}
        data-testid="workflow-name"
        style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 520 }}
        styles={{ input: { ...heading, height: 32, minHeight: 32, padding: '0 6px', borderRadius: 6, border: `1px solid ${TOKENS.border}` } }}
      />
    );
  }
  const shown = value.trim() || placeholder;
  const title = (
    <Text
      component="h2"
      m={0}
      style={{ ...heading, color: value.trim() ? TOKENS.text : TOKENS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {shown}
    </Text>
  );
  if (!canEdit) return <Box style={{ minWidth: 0 }} data-testid="workflow-title">{title}</Box>;
  return (
    <Tooltip
      label={suggested ? 'Suggested from the trigger and first step. Click to change it.' : 'Click to rename'}
      openDelay={suggested ? 0 : 500}
      withArrow
    >
      <UnstyledButton
        onClick={() => { before.current = value; setEditing(true); }}
        aria-label={`Rename: ${shown}`}
        data-testid="workflow-title"
        className="wf-inline-name"
        style={{ minWidth: 0, borderRadius: 6, padding: '0 6px', margin: '0 -6px', border: '1px solid transparent' }}
      >
        {title}
      </UnstyledButton>
    </Tooltip>
  );
}

/**
 * Names a new workflow from its trigger and first step, "Energy Premium
 * lead → broadband welcome", until someone types a name. Rendered only
 * while that is so, so a saved workflow never loads what it needs.
 */
function NameSuggester({
  trigger, steps, name, onName,
}: { trigger: WorkflowTrigger; steps: WorkflowStep[]; name: string; onName: (n: string) => void }) {
  const v = useVocab();
  const names = useNames();
  const suggested = suggestName(v, trigger, steps, names);
  useEffect(() => {
    if (name !== suggested) onName(suggested);
  }, [name, suggested, onName]);
  return null;
}

/** Names for the ids a suggested name may mention: streams, templates, stages, people. */
function useNames(): (id: string) => string | undefined {
  const campaigns = useCampaigns().data?.campaigns;
  const templates = useEmailTemplates().data?.templates;
  const pipelines = usePipelines().data?.pipelines;
  const owners = useOwnerOptions();
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const c of campaigns ?? []) m.set(c.id, c.name);
    for (const t of templates ?? []) m.set(t.id, t.name);
    for (const p of pipelines ?? []) for (const st of p.stages) m.set(st.id, st.name);
    for (const o of owners) m.set(o.value, o.label.replace(/ \(you\)$/, ''));
    return (id: string) => m.get(id);
  }, [campaigns, templates, pipelines, owners]);
}
